import { Controller } from "@hotwired/stimulus"

// Admin practitioner form: a clock beside each field label opens, under the field, the last changes of that field
// (Admin::Turbo::Profiles::Practitioners::FieldAuditHistoriesController).
// Fields are found by their `.form-label` and the first named input of their wrapper, so new fields get the clock
// without template changes; a block with no such label marks the clock's place with `data-field-history-field`
// (the field name) and its panel's with `data-field-history-row` (the panel goes after it). An input that is no
// field of the record (a note posted on save) opts out with `data-field-history-skip`. The server tells which
// fields have a history. The history is fetched as soon as the
// pointer or the focus reaches the clock (a plain fetch: a Turbo frame built in script started late), so a click
// opens it at once; the panel slides open to its loaded height.
// Class names written in full: PurgeCSS keeps only the ones it reads.
const PANEL_CLASS = "admin-audit-history-field__panel"
const OPEN_CLASS = "admin-audit-history-field__panel--open"
const INNER_CLASS = "admin-audit-history-field__panel-inner"
const BODY_CLASS = "admin-audit-history-field__panel-body"
const BUTTON_CLASS = "admin-audit-history-field__button"
// The server answers in this frame (Turbo::FrameRequireable); its content goes into the panel.
const FRAME_ID = "field-history"

export default class extends Controller {
  static values = {
    url: String,
    scope: String,
    buttonTitle: String
  }

  connect () {
    this.decorate()
    this.observer = new MutationObserver(() => this.decorate())
    this.observer.observe(this.element, { childList: true, subtree: true })
  }

  disconnect () {
    this.observer?.disconnect()
  }

  prefetch (event) {
    this.panelFor(event.currentTarget, event.params)
  }

  toggle (event) {
    const button = event.currentTarget
    const panel = this.panelFor(button, event.params)
    const expanded = panel.dataset.expanded !== "true"

    panel.dataset.expanded = String(expanded)
    if (panel.dataset.loaded === "true") panel.classList.toggle(OPEN_CLASS, expanded)
    button.setAttribute("aria-expanded", String(expanded))
  }

  // The field's panel, built (closed) and loaded the first time. +field+: the button's params (name, value).
  panelFor (button, field) {
    const anchor = button.closest("[data-field-history-field]")
    if (anchor) return this.rowPanel(anchor.closest("[data-field-history-row]") || anchor, field)

    const label = button.closest(".form-label")
    // Under the input, in the field's controls: a direct child of a `.row` wrapper would take the grid gutters.
    const container = label.nextElementSibling || label.parentElement
    let panel = container.querySelector(`:scope > .${PANEL_CLASS}`)

    if (!panel) {
      panel = this.buildPanel()
      container.append(panel)
      this.load(panel, field)
    }
    return panel
  }

  rowPanel (row, field) {
    let panel = row.nextElementSibling?.classList.contains(PANEL_CLASS) ? row.nextElementSibling : null

    if (!panel) {
      panel = this.buildPanel()
      row.after(panel)
      this.load(panel, field)
    }
    return panel
  }

  // An opening before the content arrives waits for it, so the panel grows once to its final height.
  async load (panel, { name, value }) {
    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set("field", name)
    if (value) url.searchParams.set("field_value", value)
    url.searchParams.set("frame_id", FRAME_ID)

    try {
      const response = await fetch(`${url.pathname}${url.search}`, { headers: { Accept: "text/html", "Turbo-Frame": FRAME_ID } })
      const frame = new DOMParser().parseFromString(await response.text(), "text/html").querySelector("turbo-frame")
      panel.querySelector(`.${BODY_CLASS}`).replaceChildren(...Array.from(frame?.childNodes || []))
    } finally {
      panel.dataset.loaded = "true"
      if (panel.dataset.expanded === "true") requestAnimationFrame(() => panel.classList.add(OPEN_CLASS))
    }
  }

  decorate () {
    this.element.querySelectorAll("[data-field-history-field]").forEach((anchor) => {
      if (!anchor.querySelector(`.${BUTTON_CLASS}`)) anchor.append(this.buildButton(anchor.dataset.fieldHistoryField))
    })
    this.element.querySelectorAll(".form-label").forEach((label) => {
      if (label.querySelector(`.${BUTTON_CLASS}`)) return

      const input = this.fieldInput(label.parentElement)
      // A hidden input's value tells a nested list's row type (education records: diplomas or continuing education).
      if (input) label.append(this.buildButton(input.name, input.type === "hidden" ? input.value : null))
    })
  }

  // Names compared in script: jsdom's selector engine misreads a "[" inside an attribute selector's value.
  fieldInput (wrapper) {
    const prefix = `${this.scopeValue}[`
    return Array.from(wrapper?.querySelectorAll("[name]") || []).find((input) =>
      input.name.startsWith(prefix) &&
        !input.name.endsWith("[_destroy]") &&
        !input.name.endsWith("[id]") &&
        !input.hasAttribute("data-field-history-skip")
    )
  }

  buildButton (name, value = null) {
    const button = document.createElement("button")
    button.type = "button"
    button.className = BUTTON_CLASS
    button.title = this.buttonTitleValue
    button.setAttribute("aria-label", this.buttonTitleValue)
    button.setAttribute("aria-expanded", "false")
    button.dataset.action = [
      "mouseenter->admin--field-history#prefetch",
      "focus->admin--field-history#prefetch",
      "click->admin--field-history#toggle"
    ].join(" ")
    button.setAttribute(`data-${this.identifier}-name-param`, name.replace(/\[\]$/, ""))
    if (value) button.setAttribute(`data-${this.identifier}-value-param`, value)
    button.innerHTML = '<i class="fa fa-clock-rotate-left" aria-hidden="true"></i>'
    return button
  }

  buildPanel () {
    const body = document.createElement("div")
    body.className = BODY_CLASS

    const inner = document.createElement("div")
    inner.className = INNER_CLASS
    inner.append(body)

    const panel = document.createElement("div")
    panel.className = PANEL_CLASS
    panel.append(inner)
    return panel
  }
}
